import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { clipboardCopy } from '../../utils'
import { abrViewContentDetails } from '../../utils/abr'

import { StoresContext } from '../../provider/StoreProvider'

import { useNavigate } from '../../hooks/useNavigate'
import useParamId from '../../hooks/useParamId'

import { getArticleDetail } from '../../api/article'

import { Head } from '../../components/head/Head'
import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { CenterWrap, RowBetween } from '../../components/common'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { ArtipioContentsHeader, ArtipioContents, ArtipioContentItem } from '../../components/contents'

import { ReactComponent as HomeIcon } from '../../assets/ico/detail/home_black.svg'
import { ReactComponent as ShareIcon } from '../../assets/ico/detail/share_black.svg'

export function ContentDetail() {
  const {
    commonStore: { setShowTab },
  } = useContext(StoresContext)

  const navigate = useNavigate()
  const articleId = useParamId()

  const {
    data: article,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEY.article, { articleId }],
    queryFn: () => getArticleDetail(articleId as number),
    enabled: typeof articleId === 'number',
  })

  const HEAD_INFO = {
    title: `${article?.meta?.title ?? '콘텐츠 상세'} | 콘텐츠 | 미술품 조각투자 플랫폼, 아티피오`,
    description: article?.meta?.description ?? '',
    ogTitle: `${article?.meta?.ogTitle ?? '콘텐츠 상세'} | 콘텐츠 | 미술품 조각투자 플랫폼, 아티피오`,
    ogDescription: article?.meta?.ogDescription ?? '',
    ogUrl: window.location.href,
  }

  useEffect(() => {
    setShowTab(false)
  }, [])

  useEffect(() => {
    abrViewContentDetails(article ?? {}, article?.category ?? {})
    ChannelService.track('view_content_details')
  }, [article])

  if (isPending || isError) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <Header type="SUB" />
          <CenterWrap>
            <Spinner />
          </CenterWrap>
        </Wrap>
      </>
    )
  }

  const isLastImg = article.contents?.[(article.contents?.length as number) - 1]?.type === 'image'

  const goHome = () => navigate('/home')

  const copyUrl = () => clipboardCopy(window.location.href, { message: '콘텐츠 페이지 주소가 복사되었어요.' })

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header
          type="SUB"
          rightItem={
            <HeaderRightItem $gap={16}>
              <ShareIcon onClick={copyUrl} />
              <HomeIcon onClick={goHome} />
            </HeaderRightItem>
          }
        />
        <StyledContent isLastImg={isLastImg}>
          <ArtipioContentsHeader>
            <ArtipioContentsHeader.Title>{article.title}</ArtipioContentsHeader.Title>
            <ArtipioContentsHeader.Date>{dayjs(article.createdDate).format('YYYY-MM-DD')}</ArtipioContentsHeader.Date>
          </ArtipioContentsHeader>
          <ArtipioContents>
            {article.contents?.map(({ type, content, prop }, i) => {
              const isFirstImg = type === 'image' && i === 0
              const isLastImg = type === 'image' && i === Number(article.contents?.length) - 1
              return (
                <ArtipioContentItem
                  key={i}
                  type={type}
                  content={content}
                  prop={prop}
                  isFirstImg={isFirstImg}
                  isLastImg={isLastImg}
                />
              )
            })}
          </ArtipioContents>
        </StyledContent>
      </Wrap>
    </>
  )
}

const HeaderRightItem = styled(RowBetween)`
  > * {
    cursor: pointer;
  }
`

const StyledContent = styled(Content)<{ isLastImg: boolean }>`
  ${({ isLastImg }) => (isLastImg ? '' : 'padding-bottom: 108px;')}
`
